import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Container, Grid, CircularProgress, Alert } from '@mui/material';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DocumentationSidebar from './DocumentationSidebar';
import DocumentationPage from './DocumentationPage';
import { alpha } from '@mui/material/styles';

// Helper function to get user's profile image
const getProfileImage = (user) => {
  if (!user) return null;
  // Handle Google profile images from provider data
  if (user.providerData?.[0]?.photoURL) {
    return user.providerData[0].photoURL;
  }
  // Fallback to user photoURL
  return user.photoURL;
};

const Documentation = () => {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const isInitialMount = useRef(true);

  // Fetch documentation pages
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        console.log('Fetching documentation data...');
        
        // First try to fetch users to ensure permissions are working
        let usersData = [];
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          usersData = usersSnapshot.docs.map(doc => {
            const userData = doc.data();
            return {
              uid: doc.id,
              ...userData,
              photoURL: getProfileImage(userData)
            };
          });
          console.log(`Successfully fetched ${usersData.length} users`);
          setUsers(usersData);
        } catch (userErr) {
          console.error('Error fetching users:', userErr);
          throw new Error(`Unable to access user data: ${userErr.message}`);
        }
        
        // Then fetch documentation pages
        try {
          const pagesSnapshot = await getDocs(collection(db, 'documentation'));
          const pagesData = pagesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log(`Successfully fetched ${pagesData.length} documentation pages`);
          setPages(pagesData);
        } catch (docsErr) {
          console.error('Error fetching documentation:', docsErr);
          if (docsErr.code === 'permission-denied') {
            throw new Error('You do not have permission to access documentation. Please contact your administrator.');
          } else {
            throw docsErr;
          }
        }
        
        // Don't pre-select a page - let user choose
        isInitialMount.current = false;
        
        setError(null);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        // Show a more user-friendly error message
        if (err.code === 'permission-denied') {
          setError('Access denied: You do not have permission to view documentation.');
        } else {
          setError(`${err.message || 'Failed to load documentation. Please try again later.'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []); // Empty dependency array is now correct since we use isInitialMount ref

  // Handle adding a new page
  const handleAddPage = async (newPage) => {
    try {
      setLoading(true);
      console.log('Adding new page with data:', newPage);
      
      // Create a new document reference with auto-generated ID
      const pageRef = doc(collection(db, 'documentation'));
      
      // Store current user information
      const userData = {
        uid: user.uid,
        name: user.displayName || user.email.split('@')[0],
        avatar: getProfileImage(user)
      };
      
      // Handle both string input (just title) and object input
      const pageData = {
        title: typeof newPage === 'string' ? newPage : newPage.title,
        category: typeof newPage === 'object' && newPage.category ? newPage.category : 'Uncategorized',
        content: '',
        createdBy: user.uid,
        createdAt: new Date(),
        lastUpdatedBy: user.uid,
        lastUpdatedAt: new Date(),
        createdByUser: userData,
        lastUpdatedByUser: userData,
        comments: []
      };
      
      console.log('Prepared page data:', pageData);
      
      // Set the document data with the generated ID
      await setDoc(pageRef, pageData);
      console.log('Successfully created page with ID:', pageRef.id);
      
      const newPageWithId = {
        id: pageRef.id,
        ...pageData
      };
      
      // Update local state
      setPages(prevPages => [...prevPages, newPageWithId]);
      setSelectedPage(newPageWithId);
      setError(null);
    } catch (err) {
      console.error('Error adding page:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      setError(`Failed to add page: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle updating a page
  const handleUpdatePage = async (updatedPage) => {
    try {
      setLoading(true);
      
      // Store current user information for the update
      const userData = {
        uid: user.uid,
        name: user.displayName || user.email.split('@')[0],
        avatar: getProfileImage(user)
      };
      
      const pageRef = doc(db, 'documentation', updatedPage.id);
      await updateDoc(pageRef, {
        title: updatedPage.title,
        content: updatedPage.content,
        category: updatedPage.category || 'Uncategorized',
        lastUpdatedBy: user.uid,
        lastUpdatedAt: new Date(),
        lastUpdatedByUser: userData
      });
      
      // Update local state with the user data
      const updatedPageWithUser = {
        ...updatedPage,
        lastUpdatedBy: user.uid,
        lastUpdatedAt: new Date(),
        lastUpdatedByUser: userData
      };
      
      setPages(prevPages => 
        prevPages.map(page => 
          page.id === updatedPage.id ? updatedPageWithUser : page
        )
      );
      setSelectedPage(updatedPageWithUser);
      setError(null);
    } catch (err) {
      console.error('Error updating page:', err);
      setError('Failed to update page. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle deleting a page
  const handleDeletePage = async (pageId) => {
    try {
      setLoading(true);
      
      await deleteDoc(doc(db, 'documentation', pageId));
      
      // Update local state
      setPages(prevPages => prevPages.filter(page => page.id !== pageId));
      
      // If the deleted page was selected, select another page or set to null
      if (selectedPage && selectedPage.id === pageId) {
        const remainingPages = pages.filter(page => page.id !== pageId);
        setSelectedPage(remainingPages.length > 0 ? remainingPages[0] : null);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error deleting page:', err);
      setError('Failed to delete page. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle adding a comment
  const handleAddComment = async (pageId, commentText) => {
    try {
      const pageRef = doc(db, 'documentation', pageId);
      
      const comment = {
        text: commentText,
        createdBy: user.uid,
        createdAt: new Date(),
        user: {
          uid: user.uid,
          name: user.displayName || user.email.split('@')[0],
          avatar: getProfileImage(user)
        }
      };
      
      await updateDoc(pageRef, {
        comments: arrayUnion(comment)
      });
      
      // Update local state
      setPages(prevPages => 
        prevPages.map(page => {
          if (page.id === pageId) {
            const updatedComments = [...(page.comments || []), comment];
            return { ...page, comments: updatedComments };
          }
          return page;
        })
      );
      
      // Update selected page if it's the one that got commented
      if (selectedPage && selectedPage.id === pageId) {
        setSelectedPage(prev => ({
          ...prev,
          comments: [...(prev.comments || []), comment]
        }));
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment. Please try again later.');
    }
  };
  
  // Handle deleting a comment
  const handleDeleteComment = async (pageId, commentIndex) => {
    try {
      const page = pages.find(p => p.id === pageId);
      if (!page || !page.comments || commentIndex >= page.comments.length) {
        return;
      }
      
      const commentToRemove = page.comments[commentIndex];
      const pageRef = doc(db, 'documentation', pageId);
      
      await updateDoc(pageRef, {
        comments: arrayRemove(commentToRemove)
      });
      
      // Update local state
      const updatePageComments = (page) => {
        if (page.id !== pageId) return page;
        const newComments = [...page.comments];
        newComments.splice(commentIndex, 1);
        return { ...page, comments: newComments };
      };
      
      setPages(prevPages => prevPages.map(updatePageComments));
      
      // Update selected page if it's the one that had a comment deleted
      if (selectedPage && selectedPage.id === pageId) {
        setSelectedPage(updatePageComments(selectedPage));
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment. Please try again later.');
    }
  };

  // Extract all unique categories from pages
  const availableCategories = useMemo(() => {
    const categories = new Set(['Uncategorized']);
    pages.forEach(page => {
      if (page.category) {
        categories.add(page.category);
      }
    });
    return Array.from(categories).sort();
  }, [pages]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.error.main, 0.2),
            '& .MuiAlert-icon': {
              color: 'error.main'
            }
          }}
        >
          {error}
        </Alert>
      )}
      
              <Grid 
        container 
        spacing={3} 
        sx={{ 
          height: 'calc(100vh - 229px)', 
          alignItems: 'stretch' 
        }}
      >
        {/* Sidebar */}
        <Grid 
          item 
          xs={12} 
          md={3} 
          lg={2.5} 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0,
            height: '100%' 
          }}
        >
          <DocumentationSidebar
            pages={pages}
            selectedPageId={selectedPage?.id}
            onSelectPage={(pageId) => {
              const page = pages.find(p => p.id === pageId);
              setSelectedPage(page);
            }}
            onAddPage={handleAddPage}
            loading={loading}
          />
        </Grid>
        
        {/* Main Content */}
        <Grid 
          item 
          xs={12} 
          md={9} 
          lg={9.5} 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0,
            height: '100%' 
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <DocumentationPage
              page={selectedPage}
              currentUser={user}
              users={users}
              categories={availableCategories}
              onUpdatePage={handleUpdatePage}
              onDeletePage={handleDeletePage}
              onAddComment={handleAddComment}
              onDeleteComment={handleDeleteComment}
            />
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default Documentation; 